package org.catharsis.spendtel.feature.presentation.settings

import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.navigation.NavHostController
import androidx.navigation.compose.currentBackStackEntryAsState
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.feature.data.BillingEntitlement
import org.catharsis.spendtel.feature.data.FinancialGoal
import org.catharsis.spendtel.feature.presentation.HomeEffect
import org.catharsis.spendtel.feature.presentation.HomeEvent
import org.catharsis.spendtel.feature.presentation.HomeViewModel
import org.catharsis.spendtel.feature.presentation.PaywallUiState
import org.catharsis.spendtel.feature.presentation.auth.AuthenticationEffect
import org.catharsis.spendtel.feature.presentation.auth.AuthenticationEvent
import org.catharsis.spendtel.feature.presentation.auth.AuthenticationViewModel
import org.koin.compose.viewmodel.koinViewModel

class SettingsScreenBindings(
    val goalTitle: String,
    val entitlement: BillingEntitlement?,
    val isPurchasing: Boolean,
    val paywall: PaywallUiState?,
    val isDeletingAccount: Boolean,
    val snackbarHostState: SnackbarHostState,
    val onBuyReport: () -> Unit,
    val onChangeGoal: () -> Unit,
    val onDeleteAccount: () -> Unit,
    val onDismissPaywall: () -> Unit,
    val onPurchaseReport: () -> Unit,
)

@Composable
fun rememberSettingsScreenBindings(
    navHostController: NavHostController,
    authViewModel: AuthenticationViewModel = koinViewModel(),
    homeViewModel: HomeViewModel = koinViewModel(),
): SettingsScreenBindings {
    val authState by authViewModel.authState.collectAsState()
    val homeState by homeViewModel.state.collectAsState()
    val currentBackStackEntry by navHostController.currentBackStackEntryAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    val currentGoal = FinancialGoal.fromId(authState.currentUser?.financialGoal)

    LaunchedEffect(currentBackStackEntry) {
        authViewModel.refreshCurrentUser()
        homeViewModel.postEvent(HomeEvent.OnRefreshEntitlement)
    }

    LaunchedEffect(Unit) {
        homeViewModel.effect.collect { effect ->
            when (effect) {
                is HomeEffect.OnError -> snackbarHostState.showSnackbar(effect.message)
                is HomeEffect.OnPurchaseComplete -> snackbarHostState.showSnackbar(effect.message)
                HomeEffect.OnPurchaseProcessing -> snackbarHostState.showSnackbar("Confirming your payment…")
                else -> Unit
            }
        }
    }

    LaunchedEffect(Unit) {
        authViewModel.effect.collect { effect ->
            when (effect) {
                is AuthenticationEffect.OnDeleteAccountResult -> {
                    snackbarHostState.showSnackbar(effect.message)
                }
                else -> Unit
            }
        }
    }

    return SettingsScreenBindings(
        goalTitle = currentGoal?.title ?: "Not set",
        entitlement = homeState.entitlement,
        isPurchasing = homeState.isPurchasing || homeState.paywall?.isPurchasing == true,
        paywall = homeState.paywall,
        isDeletingAccount = authState.isDeletingAccount,
        snackbarHostState = snackbarHostState,
        onBuyReport = { homeViewModel.postEvent(HomeEvent.OnBuyReportCredit) },
        onChangeGoal = { navHostController.navigate(AppRoutes.EditFinancialGoal.name) },
        onDeleteAccount = { authViewModel.postEvent(AuthenticationEvent.OnDeleteAccount) },
        onDismissPaywall = { homeViewModel.postEvent(HomeEvent.OnDismissPaywall) },
        onPurchaseReport = { homeViewModel.postEvent(HomeEvent.OnPurchaseReport) },
    )
}
